<template>
  <div class="panorama-page p-3 p-md-4 dark-theme">
    <div class="d-flex align-items-center justify-content-between mb-4">
      <h2 class="text-white mb-0 font-weight-bold tracking-wide">
        <b-icon icon="display" class="mr-2 text-warning"></b-icon>策略全景解析
      </h2>
      <b-button variant="outline-light" class="rounded-pill px-4 shadow-sm" @click="$router.back()">
        <b-icon icon="arrow-left" class="mr-1"></b-icon> 返回
      </b-button>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="warning" label="Loading..."></b-spinner>
      <div class="text-muted-gold mt-3">正在获取今日监控数据...</div>
    </div>

    <div v-else-if="topics.length === 0" class="text-center py-5">
      <b-icon icon="inbox" font-scale="3" class="text-muted mb-3"></b-icon>
      <div class="text-muted">今日无监控题材或暂未获取到数据</div>
    </div>

    <div v-else class="row no-gutters custom-row-5">
      <div v-for="(topic, index) in topics" :key="index" class="col-12 col-md-4 col-xl-20 mb-2 px-1">
        <b-card class="bg-card-dark border-light-10 shadow-sm h-100 rounded-lg overflow-hidden" body-class="p-1 p-md-2">
          <!-- 头部 -->
          <div class="d-flex justify-content-between align-items-start mb-2 pb-2 border-bottom border-light-10">
            <div>
              <h6 class="mb-1 text-white font-weight-bold text-truncate" style="max-width: 150px;" :title="topic.topic_name">
                {{ topic.topic_name }}
              </h6>
              <b-badge variant="soft-warning" class="xx-small mb-1 d-inline-block">共 {{ topic.total_candidates }} 备选</b-badge>
              <div class="text-muted xx-small" v-if="topic.stats">
                涨跌: <span class="text-danger">{{ topic.stats.up_count }}</span>/<span class="text-success">{{ topic.stats.down_count }}</span>
                <span class="mx-1">|</span>
                均: <span :class="getPnlClass(topic.stats.avg_pct)">{{ topic.stats.avg_pct.toFixed(2) }}%</span>
              </div>
              <div class="text-muted xx-small mt-1 text-truncate" style="max-width: 180px;" v-if="topic.stats" :title="topic.stats.main_leader + ' (主) / ' + topic.stats.gem_leader + ' (创)'">
                领涨: <span class="text-white">{{ topic.stats.main_leader }} / {{ topic.stats.gem_leader }}</span>
              </div>
              <div class="text-muted xx-small mt-1 text-truncate" style="max-width: 180px;" :title="topic.stats.limit_ups" v-if="topic.stats">
                涨停: <span class="text-danger">{{ topic.stats.limit_ups }}</span>
              </div>
            </div>
            <div class="text-right">
              <div class="text-muted xx-small">触发/限额</div>
              <div class="text-white xx-small mt-1">
                <span class="text-danger font-weight-bold">{{ topic.alerts.length }}</span>次
                <span class="mx-1">|</span>
                主<span :class="{'text-warning': topic.counts['主板'] >= 2}">{{ topic.counts['主板'] || 0 }}/2</span>
                <span class="mx-1">|</span>
                创<span :class="{'text-warning': topic.counts['双创'] >= 1}">{{ topic.counts['双创'] || 0 }}/1</span>
              </div>
            </div>
          </div>
          
          <!-- 备选股列表 -->
          <div class="table-responsive">
            <table class="table table-dark table-hover table-borderless table-sm mb-0 compact-table xx-small">
              <thead>
                <tr class="text-muted border-bottom border-light-10">
                  <th style="width: 35%">股票</th>
                  <th class="text-right">现价</th>
                  <th class="text-right">额/市值(亿)</th>
                  <th class="text-right">涨跌幅</th>
                  <th class="text-center" style="width: 15%">状态</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="stock in getDisplayCandidates(topic.candidates, topic.topic_name)" :key="stock.code6">
                  <td class="align-middle">
                    <a href="javascript:void(0)" @click="showStockInfo(stock.code6, stock.name)" class="text-white text-decoration-none hover-primary text-truncate d-inline-block" style="max-width: 60px;">
                      {{ stock.name }}
                    </a>
                    <span class="text-muted ml-1" style="transform: scale(0.85); display: inline-block;">{{ stock.code6 }}</span>
                  </td>
                  <td class="text-right align-middle font-weight-bold" :class="getPnlClass(stock.pct_change)">
                    {{ stock.price.toFixed(2) }}
                  </td>
                  <td class="text-right align-middle text-muted">
                    {{ (stock.amount / 100000000).toFixed(1) }}/{{ (stock.mkv / 100000000).toFixed(1) }}
                  </td>
                  <td class="text-right align-middle font-weight-bold" :class="getPnlClass(stock.pct_change)">
                    {{ stock.pct_change > 0 ? '+' : '' }}{{ stock.pct_change.toFixed(2) }}%
                  </td>
                  <td class="text-center align-middle">
                    <b-badge :variant="stock.triggered ? 'success' : 'secondary'" style="font-size: 0.65rem; padding: 0.15em 0.3em;">
                      {{ stock.triggered ? '已触发' : '未触发' }}
                    </b-badge>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="topic.candidates.length > 10" class="text-center mt-1">
              <b-button variant="link" size="sm" class="text-muted xx-small p-0" @click="toggleExpand(topic.topic_name)">
                {{ expandedTopics[topic.topic_name] ? '收起' : `查看更多 (${topic.candidates.length - 10})` }}
                <b-icon :icon="expandedTopics[topic.topic_name] ? 'chevron-up' : 'chevron-down'"></b-icon>
              </b-button>
            </div>
          </div>
        </b-card>
      </div>
    </div>
    
    <!-- 引入个股侧边栏 -->
    <StockInfoSidebar 
      :symbol_code="sidebarSymbolCode" 
      :symbol_name="sidebarSymbolName" 
    />
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'

export default {
  name: 'WonderStrategyPanorama',
  components: {
    StockInfoSidebar
  },
  data() {
    return {
      loading: true,
      topics: [],
      expandedTopics: {},
      sidebarSymbolCode: '',
      sidebarSymbolName: ''
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    toggleExpand(topicName) {
      this.$set(this.expandedTopics, topicName, !this.expandedTopics[topicName])
    },
    getDisplayCandidates(candidates, topicName) {
      if (!candidates) return []
      // Fix: Need to use the passed topicName to check expand state
      const isExpanded = this.expandedTopics[topicName]
      if (isExpanded || candidates.length <= 10) {
        return candidates
      }
      return candidates.slice(0, 10)
    },
    showStockInfo(code, name) {
      this.sidebarSymbolCode = code || ''
      this.sidebarSymbolName = name || ''
      this.$nextTick(() => {
        this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
      })
    },
    async fetchData() {
      this.loading = true
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/panorama',
          method: 'get'
        })
        if (res.data && (res.data.code === 200 || res.data.code === 20000)) {
          this.topics = res.data.data
        } else {
          this.$bvToast.toast(res.data?.msg || res.data?.message || '获取数据失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('网络请求异常', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loading = false
      }
    },
    getPnlClass(val) {
      if (val > 0) return 'text-danger'
      if (val < 0) return 'text-success'
      return 'text-muted'
    }
  }
}
</script>

<style scoped>
.panorama-page {
  min-height: 100vh;
  background-color: #0f1219;
}
.bg-card-dark {
  background-color: #1a1e29;
}
.border-light-10 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}
.text-muted-gold {
  color: #bfa77b !important;
}
.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.15) !important;
  color: #ffc107 !important;
}
.table-dark {
  background-color: transparent;
}
.hover-primary:hover {
  color: var(--primary) !important;
}
.table-hover tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.05);
}
.x-small {
  font-size: 0.75rem;
}
.xx-small {
  font-size: 0.65rem;
}
.compact-table td, .compact-table th {
  padding: 0.15rem 0.2rem !important;
}
.custom-row-5 > .col-xl-20 {
  flex: 0 0 20%;
  max-width: 20%;
}
</style>